import {
  LoadableState,
  defaultLoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { CreditStatusResponse } from '~/services/home/contracts/FinanceData';

type FinanceContextType = {
  financeInfo: LoadableState<Partial<CreditStatusResponse>>;
  fetchFinanceTrigger: number;
  triggerFetchFinance: () => void;
};

const defaultFinanceContext: FinanceContextType = {
  financeInfo: defaultLoadableState<Partial<CreditStatusResponse>>({}),
  fetchFinanceTrigger: 0,
  triggerFetchFinance() {}
};

export const FinanceContext = createContext<FinanceContextType>(
  defaultFinanceContext
);

export function FinanceContextProvider({ children }: React.PropsWithChildren) {
  const financeInfo = useLoadableState<Partial<CreditStatusResponse>>({});
  const [fetchFinanceTrigger, setTrigger] = useState(
    defaultFinanceContext.fetchFinanceTrigger
  );

  const triggerFetchFinance = useCallback(() => {
    setTrigger(Date.now());
  }, []);

  const value = useMemo<FinanceContextType>(
    () => ({
      financeInfo,
      fetchFinanceTrigger,
      triggerFetchFinance
    }),
    [financeInfo, fetchFinanceTrigger, triggerFetchFinance]
  );

  return (
    <FinanceContext.Provider value={value}>{children}</FinanceContext.Provider>
  );
}

export const useHasValidSoftPull = (): boolean => {
  const ctx = useContext(FinanceContext).financeInfo;

  return ctx.data.creditStatus
    ? ctx.data.creditStatus?.hasValidSoftPull
    : false;
};

export const useHasExpiredSoftpull = (): boolean => {
  const ctx = useContext(FinanceContext).financeInfo;

  return ctx.data.creditStatus
    ? ctx.data.creditStatus.hasExpiredSoftPull
    : false;
};

export const useMaxMonthly = (): number => {
  const ctx = useContext(FinanceContext).financeInfo;

  return ctx.data.financingPolicy ? ctx.data.financingPolicy.maxPayment : 0;
};

export const useIsFinanceLoaded = (): boolean => {
  const ctx = useContext(FinanceContext).financeInfo;

  return ctx.isLoaded;
};

export const useGetExpirationDate = (): string | null => {
  const ctx = useContext(FinanceContext).financeInfo;

  return ctx.data.creditApplicationExpirationUtc ?? null;
};

export const useIsExpiredSoon = (): boolean => {
  const ctx = useContext(FinanceContext).financeInfo;
  const hasValidSoftPull = useHasValidSoftPull();

  const getIsWithin3days = (date: string) => {
    const now = new Date();
    const expirationDate = new Date(date);
    const diff = expirationDate.getTime() - now.getTime();

    return diff / (1000 * 60 * 60 * 24) <= 3;
  };

  return hasValidSoftPull && ctx.data.creditApplicationExpirationUtc
    ? getIsWithin3days(ctx.data.creditApplicationExpirationUtc)
    : false;
};

export default FinanceContext;
